import React from 'react';
import type { WrappedComponentProps } from 'react-intl';
import { defineMessages, injectIntl, FormattedMessage } from 'react-intl';
import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import EmptyState from '@atlaskit/empty-state';
import Button from '@atlaskit/button/standard-button';
import Image from '@atlaskit/image';

import { useSpaceEnabledContentTypes } from '@confluence/create-blank-fabric-page/entry-points/useSpaceEnabledContentTypes.ts';
import { usePageSpaceKey } from '@confluence/page-context';

import { useCreateDoc } from './useCreateDoc.tsx';
import PopularFeedLight from './images/PopularFeedLight.svg';
import PopularFeedDark from './images/PopularFeedDark.svg';

const messages = defineMessages({
	heading: {
		id: 'feed.end-of-popular.header',
		defaultMessage: "Nothing's trending just yet",
		description: 'Heading when popular feed has no content',
	},
	description: {
		id: 'feed.end-of-popular.description',
		defaultMessage:
			'Kick things off by creating something others will want to read, react to, and share.',
		description: 'Description encouraging content creation',
	},
	ctaCreateDoc: {
		id: 'feed.end-of-popular.cta.create-doc',
		defaultMessage: 'Create doc',
		description: 'Button text to create new live doc',
	},
	ctaCreatePage: {
		id: 'feed.end-of-popular.cta.create-page',
		defaultMessage: 'Create page',
		description: 'Button text to create new page',
	},
});

const endOfFeedImageStyles = css({
	maxWidth: '74px',
	maxHeight: '74px',
});

const dividerContainerStyles = css({
	borderTop: `${token('border.width')} solid ${token('color.border')}`,
	paddingTop: token('space.200'),
	marginTop: token('space.200'),
});

const EndOfPopularFeedComponent: React.FC<WrappedComponentProps> = ({ intl }) => {
	const { createDoc } = useCreateDoc();
	const [spaceKey] = usePageSpaceKey();
	const { isCreateLiveDocEnabled } = useSpaceEnabledContentTypes(spaceKey);

	const actionButton = (
		<Button onClick={createDoc}>
			<FormattedMessage
				{...(isCreateLiveDocEnabled ? messages.ctaCreateDoc : messages.ctaCreatePage)}
			/>
		</Button>
	);

	return (
		<div css={dividerContainerStyles}>
			<EmptyState
				header={intl.formatMessage(messages.heading)}
				description={<FormattedMessage {...messages.description} />}
				renderImage={() => (
					<Image
						css={endOfFeedImageStyles}
						src={PopularFeedLight}
						srcDark={PopularFeedDark}
						width="74"
						height="74"
						alt=""
					/>
				)}
				primaryAction={actionButton}
			/>
		</div>
	);
};

export const EndOfPopularFeed = injectIntl(EndOfPopularFeedComponent);
